import { isExperimentPreview } from 'lib/experiments/preview'
import type { AnyAction, Middleware } from 'redux'
import {
  API_CALL_SUCCESS,
  SET_CHERTO_EXPERIMENTS,
  SET_LAB_FEATURE_FLAGS,
  SET_OPTIMIZELY_EXPERIMENTS,
  SET_OPTIMIZELY_FEATURE_FLAGS,
} from 'actions/actionConstants'
import { FETCH_LAB_FEATURE_FLAG_DECISIONS, FETCH_LAB_REMOTE_CONFIG } from 'actions/apiActionConstants'
import {
  getExperimentPanelOverrides,
  getOptimizelyExperimentOverridesForDecision,
  type ExperimentPanelOverrides,
} from 'lib/experiments/experimentPanelPersistence'

const experimentDecisionActionTypes = new Set([
  SET_CHERTO_EXPERIMENTS,
  SET_LAB_FEATURE_FLAGS,
  SET_OPTIMIZELY_EXPERIMENTS,
  SET_OPTIMIZELY_FEATURE_FLAGS,
])

function getOverriddenLabRemoteConfigData(
  remoteConfigs: Record<string, App.LabRemoteConfigResult>,
  overrides: ExperimentPanelOverrides,
): Record<string, App.LabRemoteConfigResult> {
  return Object.fromEntries(Object.entries(remoteConfigs).map(([flagKey, remoteConfig]) => {
    const overrideValue = overrides.labRemoteConfigFlags[flagKey]
    if (overrideValue === undefined) return [flagKey, remoteConfig]

    return [flagKey, {
      ...remoteConfig,
      key: flagKey,
      value: overrideValue,
      error: undefined,
    }]
  }))
}

function getOverriddenDecisionAction(state: App.State, action: AnyAction): AnyAction {
  const isLabDecisionResponse = action.type === API_CALL_SUCCESS && action.api === FETCH_LAB_FEATURE_FLAG_DECISIONS
  const isLabRemoteConfigResponse = action.type === API_CALL_SUCCESS && action.api === FETCH_LAB_REMOTE_CONFIG
  if (!experimentDecisionActionTypes.has(action.type) && !isLabDecisionResponse && !isLabRemoteConfigResponse) return action

  if (isExperimentPreview(state) && (action.type === SET_OPTIMIZELY_EXPERIMENTS || action.type === SET_CHERTO_EXPERIMENTS)) return action

  const overrides = getExperimentPanelOverrides(state)
  if (!overrides) return action

  switch (action.type) {
    case SET_OPTIMIZELY_EXPERIMENTS: {
      const experiments = action.experiments as Record<string, App.OptimizelyExperimentDetails | undefined>
      const experimentOverrides = getOptimizelyExperimentOverridesForDecision(state, experiments)
      if (!experimentOverrides) return action

      return {
        ...action,
        experiments: Object.fromEntries(Object.entries(experiments).map(([experimentId, experiment]) => [
          experimentId,
          experimentOverrides[experimentId] === undefined || !experiment ? experiment : {
            ...experiment,
            enabled: experimentOverrides[experimentId] !== 'off',
            triggered: true,
            variationKey: experimentOverrides[experimentId],
          },
        ])),
      }
    }
    case SET_OPTIMIZELY_FEATURE_FLAGS:
      return {
        ...action,
        flags: { ...action.flags, ...overrides.optimizelyFeatureFlags },
      }
    case SET_CHERTO_EXPERIMENTS: {
      const experiments = action.experiments as Record<string, App.ChertoExperimentDetails | undefined>

      return {
        ...action,
        experiments: Object.fromEntries(Object.entries(experiments).map(([experimentId, experiment]) => [
          experimentId,
          overrides.chertoExperiments[experimentId] === undefined ? experiment : {
            ...experiment,
            enabled: overrides.chertoExperiments[experimentId] !== 'off',
            experimentId,
            provider: 'cherto',
            triggered: true,
            variationKey: overrides.chertoExperiments[experimentId],
          },
        ])),
      }
    }
    case SET_LAB_FEATURE_FLAGS: {
      const labFlags = action.labFlags as Record<string, App.LabFeatureFlagResult>

      return {
        ...action,
        labFlags: Object.fromEntries(Object.entries(labFlags).map(([flagKey, labFlag]) => [
          flagKey,
          overrides.labFeatureFlags[flagKey] === undefined ? labFlag : {
            ...labFlag,
            enabled: overrides.labFeatureFlags[flagKey],
            key: flagKey,
          },
        ])),
      }
    }
    case API_CALL_SUCCESS: {
      if (action.api === FETCH_LAB_FEATURE_FLAG_DECISIONS) {
        const labFlags = action.data as Record<string, App.LabFeatureFlagResult>

        return {
          ...action,
          data: Object.fromEntries(Object.entries(labFlags).map(([flagKey, labFlag]) => [
            flagKey,
            overrides.labFeatureFlags[flagKey] === undefined ? labFlag : {
              ...labFlag,
              enabled: overrides.labFeatureFlags[flagKey],
              key: flagKey,
            },
          ])),
        }
      }

      if (action.api === FETCH_LAB_REMOTE_CONFIG) {
        const remoteConfigs = action.data as Record<string, App.LabRemoteConfigResult>

        return {
          ...action,
          data: getOverriddenLabRemoteConfigData(remoteConfigs, overrides),
        }
      }

      return action
    }
    default:
      return action
  }
}

const experimentPanelPersistenceMiddleware: Middleware<{}, App.State> = store => next => (action: AnyAction) => {
  return next(getOverriddenDecisionAction(store.getState(), action))
}

export default experimentPanelPersistenceMiddleware
